import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Info, X } from 'lucide-react';
import { Card } from './SubscriptionAppCard';
import { Button } from './SubscriptionAppButton';
const shopifyBillingUrl = 'https://admin.shopify.com/settings/billing';
const billingPagePath = '/settings/account-billing';
/** Prototype-only preview values for the billing banners */
export type BillingPreviewState = 'none' | 'trial' | 'overdue' | 'plan-limit';
export interface BillingStatusBannerProps {
  /** Shopify account has an unpaid invoice */
  paymentOverdue?: boolean;
  /** Winery has passed what their current plan includes */
  planLimitReached?: boolean;
  inTrial?: boolean;
  trialDaysRemaining?: number;
  trialTotalDays?: number;
  trialFirstChargeDate?: string;
  trialFirstChargeAmount?: number;
  /** Plan the winery would move up to when they pass their current plan */
  upgradePlanName?: string;
  /** Optional in-app handler for the plan move, used on the billing page */
  onMoveToPlan?: () => void;
}
type Severity = 'critical' | 'caution' | 'info';
const formatUsd = (amount: number): string => '$' + amount.toLocaleString('en-US', {
  minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  maximumFractionDigits: 2
});
export function billingPreviewProps(state: BillingPreviewState): BillingStatusBannerProps {
  return {
    paymentOverdue: state === 'overdue',
    planLimitReached: state === 'plan-limit',
    inTrial: state === 'trial'
  };
}
/** Prototype-only control for viewing each banner in place */
export function BillingStatePreviewControl({
  value,
  onChange,
  label = 'Account state:'
}: {
  value: BillingPreviewState;
  onChange: (state: BillingPreviewState) => void;
  label?: string;
}) {
  const options: {
    id: BillingPreviewState;
    label: string;
  }[] = [{
    id: 'none',
    label: 'None'
  }, {
    id: 'trial',
    label: 'Trial'
  }, {
    id: 'overdue',
    label: 'Payment overdue'
  }, {
    id: 'plan-limit',
    label: 'Plan limit reached'
  }];
  return <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-199" data-mp="7vwbfn5ltpwv">
      <span className="text-[13px] font-medium text-[#64748B]" data-id="element-200" data-mp="cvir038wni8u">{label}</span>
      <div className="inline-flex w-fit flex-wrap rounded-md border border-[#CBD5E1] bg-white p-1" role="group" aria-label="Prototype account state preview" data-id="element-201" data-mp="hnk23luc72xa">
        {options.map(option => <button key={option.id} type="button" onClick={() => onChange(option.id)} aria-pressed={value === option.id} className={`h-[32px] px-3 rounded-[4px] text-[13px] font-medium transition-colors ${value === option.id ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-202" data-mp="3l525nh32tnrw">
            {option.label}
          </button>)}
      </div>
      <span className="text-[12px] text-[#94A3B8]" data-id="element-203" data-mp="3078d9udlqvt">Prototype control</span>
    </div>;
}
export function BillingStatusBanner({
  paymentOverdue = false,
  planLimitReached = false,
  inTrial = false,
  trialDaysRemaining = 4,
  trialTotalDays = 7,
  trialFirstChargeDate = '15 Jan 2026',
  trialFirstChargeAmount = 299,
  upgradePlanName = 'Growth',
  onMoveToPlan
}: BillingStatusBannerProps) {
  const navigate = useNavigate();
  const [trialDismissed, setTrialDismissed] = useState(false);
  // The trial banner returns for the final two days even once dismissed.
  const trialVisible = inTrial && (!trialDismissed || trialDaysRemaining <= 2);

  // At most one banner shows, picked by severity.
  type BannerKind = 'overdue' | 'plan-limit' | 'trial';
  const kind: BannerKind | null = paymentOverdue ? 'overdue' : planLimitReached ? 'plan-limit' : trialVisible ? 'trial' : null;
  if (!kind) {
    return null;
  }
  const moveToPlan = () => {
    if (onMoveToPlan) {
      onMoveToPlan();
      return;
    }
    navigate(billingPagePath);
  };
  const openShopify = () => window.open(shopifyBillingUrl, '_blank', 'noopener,noreferrer');
  const content: Record<BannerKind, {
    severity: Severity;
    title: string;
    message: string;
    action: React.ReactNode;
    dismissible?: boolean;
  }> = {
    overdue: {
      severity: 'critical',
      title: "WinePik can't be billed right now",
      message: 'Your Shopify account has an unpaid invoice. Your club and loyalty programs keep running and your members are unaffected, but some admin features will be restricted if it stays unpaid.',
      action: <Button variant="primary" onClick={openShopify} iconRight={<ExternalLink className="w-4 h-4" data-id="element-205" data-mp="2ahotha949v3t" />} data-id="element-204" data-mp="2obpuybrgdw7v">
          Pay in Shopify
        </Button>
    },
    'plan-limit': {
      severity: 'caution',
      title: "You've passed what your plan includes",
      message: 'Member billing, orders and loyalty continue as normal. Moving up keeps your club growing and keeps your costs predictable.',
      action: <Button variant="primary" onClick={moveToPlan} data-id="element-206" data-mp="25h11omd2da8j">
          Move to {upgradePlanName}
        </Button>
    },
    trial: {
      severity: 'info',
      title: `${trialDaysRemaining} days left in your ${trialTotalDays} day trial`,
      message: `Your first charge of ${formatUsd(trialFirstChargeAmount)} USD lands on ${trialFirstChargeDate}. Your card is already approved, so there's nothing to do.`,
      action: <Button variant="secondary" onClick={openShopify} iconRight={<ExternalLink className="w-4 h-4" data-id="element-208" data-mp="1lywu697hjvdn" />} data-id="element-207" data-mp="1lxc0elszwggu">
          Manage in Shopify
        </Button>,
      dismissible: true
    }
  };
  const banner = content[kind];
  const shell = banner.severity === 'critical' ? {
    border: 'border-[#FCA5A5]',
    bg: 'bg-[#FEF2F2]',
    icon: 'text-[#DC2626]',
    heading: 'text-[#991B1B]',
    body: 'text-[#991B1B]'
  } : banner.severity === 'caution' ? {
    border: 'border-[#FDE68A]',
    bg: 'bg-[#FEF3C7]',
    icon: 'text-[#D97706]',
    heading: 'text-[#92400E]',
    body: 'text-[#92400E]'
  } : {
    border: 'border-[#7DD3FC]',
    bg: 'bg-[#E0F2FE]',
    icon: 'text-[#0369A1]',
    heading: 'text-[#0C4A6E]',
    body: 'text-[#0369A1]'
  };
  return <Card padding="none" className={shell.border} data-id="element-209" data-mp="29or86u3jjpzh">
      <div className={`p-6 rounded-lg flex flex-col gap-4 md:flex-row md:items-start md:justify-between ${shell.bg}`} data-id="element-210" data-mp="17a097vugehu1">
        <div className="flex items-start gap-3" data-id="element-211" data-mp="1c81rwfnywf4z">
          {banner.severity === 'info' ? <Info className={`w-5 h-5 flex-shrink-0 mt-0.5 ${shell.icon}`} data-id="element-212" data-mp="1g9wudnhmwykc" /> : <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${shell.icon}`} data-id="element-213" data-mp="1g8c2k5mpftq6" />}
          <div data-id="element-214" data-mp="1l2g94ns25zqq">
            <h2 className={`text-[16px] font-semibold mb-1 ${shell.heading}`} data-id="element-215" data-mp="1l2zrg5lmeq5a">
              {banner.title}
            </h2>
            <p className={`text-[14px] ${shell.body}`} data-id="element-216" data-mp="1uz2uddoaqsta">{banner.message}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-shrink-0" data-id="element-217" data-mp="1zxw7h4zo8hsx">
          {banner.action}
          <button type="button" onClick={() => navigate(billingPagePath)} className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-218" data-mp="23zr4vyeheb1f">
            View billing
          </button>
          {banner.dismissible && <button type="button" onClick={() => setTrialDismissed(true)} aria-label="Dismiss" className="text-[#0369A1] hover:text-[#0C4A6E] transition-colors" data-id="element-219" data-mp="9n1evrr8hlzb">
              <X className="w-4 h-4" data-id="element-220" data-mp="3thendb81b0ml" />
            </button>}
        </div>
      </div>
    </Card>;
}